import { Button } from "@msuite/ui/button";
import { DataGrid } from "@msuite/ui/data-grid";
import { Divider } from "@msuite/ui/divider";
import { Icon } from "@msuite/ui/icons";
import { Email, Phone } from "@msuite/ui/link";
import { useTranslation } from "@msuite/ui/localization";
import { useQuery } from "@msuite/ui/query";
import { StatusIndicator } from "@msuite/ui/status-indicator";
import { responseHandler } from "@msuite/ui/utils";
import { getAddressString, guardString } from "@msuite/utils/strings";
import React from "react";
import { client } from "@/hono";

/** Props Interface */
interface ContactPopoverProps {
	clientId: string | undefined;
}

export function ContactPopover({ clientId }: ContactPopoverProps) {
	/** Hooks */
	const { t } = useTranslation();

	/** Query */
	const { data: contact } = useQuery({
		queryKey: ["contacts", clientId ?? ""],
		queryFn: async () => {
			if (!clientId) return null;
			const response = await client.contacts[":contactId"].$get({
				param: {
					contactId: clientId,
				},
			});
			const data = await responseHandler(response);
			return data.contact;
		},
		enabled: !!clientId,
	});

	/** Functions */
	function handleOnNavigate() {
		window.open(`/contacts/${clientId}`);
	}

	/** Render */
	return (
		<React.Fragment>
			<div className="flex flex-col gap-2 w-[450px]">
				<p className="font-semibold">{contact?.name}</p>
				<Divider variant="card" />
				<DataGrid>
					{contact?._type && (
						<DataGrid.Row>
							<DataGrid.Label>{t("general.type")}</DataGrid.Label>
							<DataGrid.Value className="h-stack">
								<StatusIndicator color={contact?._type?.color} />
								{guardString(contact?._type?.name)}
							</DataGrid.Value>
						</DataGrid.Row>
					)}
					<DataGrid.Row>
						<DataGrid.Label>{t("address.address")}</DataGrid.Label>
						<DataGrid.Value>{getAddressString(contact)}</DataGrid.Value>
					</DataGrid.Row>
					<DataGrid.Row>
						<DataGrid.Label>{t("personnel.fields.email")}</DataGrid.Label>
						<DataGrid.Cell>
							<Email>{guardString(contact?.email)}</Email>
						</DataGrid.Cell>
					</DataGrid.Row>
					<DataGrid.Row>
						<DataGrid.Label>{t("personnel.fields.phone")}</DataGrid.Label>
						<DataGrid.Cell>
							<Phone>{guardString(contact?.phone)}</Phone>
						</DataGrid.Cell>
					</DataGrid.Row>
					<DataGrid.Divider />
					<div className="flex flex-row gap-2 col-span-2">
						<Button
							variant="white"
							onClick={handleOnNavigate}
							icon={<Icon name="ExternalLink" />}
						>
							{t("general.information")}
						</Button>
					</div>
				</DataGrid>
			</div>
		</React.Fragment>
	);
}
